import React from "react";

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: "none" | "sm" | "md" | "lg" | "full";
}

const Skeleton: React.FC<SkeletonProps> = ({
  className = "",
  width,
  height,
  rounded = "md",
}) => {
  const roundedClasses = {
    none: "",
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    full: "rounded-full",
  };

  return (
    <div
      className={`relative overflow-hidden bg-gray-100 ${roundedClasses[rounded]} ${className}`}
      style={{
        width: width,
        height: height,
      }}
    >
      {/* Shimmer Effect */}
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
    </div>
  );
};

// Product Card Skeleton
export const ProductCardSkeleton: React.FC = () => (
  <div className="flex flex-col space-y-3">
    <Skeleton className="w-full aspect-square" rounded="lg" />
    <div className="space-y-2">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
      <div className="flex justify-between items-center">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-4 w-12" rounded="full" />
      </div>
    </div>
  </div>
);

// Product Grid Skeleton
export const ProductGridSkeleton: React.FC<{ count?: number }> = ({
  count = 6,
}) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full">
    {Array.from({ length: count }).map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </div>
);

// Category Tags Skeleton
export const CategoryTagsSkeleton: React.FC<{ count?: number }> = ({
  count = 5,
}) => (
  <div className="flex w-full gap-2 overflow-x-auto scrollbar-hide md:gap-4">
    {Array.from({ length: count }).map((_, index) => {
      // Vary the width to better match actual content
      const widths = [
        "w-16",
        "w-20",
        "w-24",
        "w-20",
        "w-28",
        "w-22",
        "w-18",
        "w-26",
      ];
      const widthClass = widths[index % widths.length];

      return (
        <Skeleton
          key={index}
          className={`h-8 px-4 flex-shrink-0 ${widthClass}`}
          rounded="full"
        />
      );
    })}
  </div>
);

// Sort Skeleton
export const SortSkeleton: React.FC<{ widthClass?: string }> = ({
  widthClass = "w-24",
}) => (
  <div
    className={`${widthClass} rounded-md bg-gray-200 animate-pulse`}
    style={{ height: "45.6px" }}
  />
);

// Results Title Skeleton
export const ResultsTitleSkeleton: React.FC<{
  showSort?: boolean;
  sortWidthClass?: string;
}> = ({ showSort = true, sortWidthClass }) => (
  <div className="flex items-center justify-between w-full">
    <Skeleton className="h-6 w-40 align-middle" />
    {showSort && (
      <div className="lg:flex hidden">
        <SortSkeleton widthClass={sortWidthClass} />
      </div>
    )}
  </div>
);

// Header Section Skeleton
export const HeaderSectionSkeleton: React.FC = () => (
  <div className="w-full mb-2 flex items-center">
    <Skeleton className="h-8 w-64" />
  </div>
);

// Filter Panel Skeleton
export const FilterPanelSkeleton: React.FC = () => (
  <div className="space-y-8 py-6 lg:py-0">
    {/* Title Section */}
    <div className="flex items-center justify-between mb-6 px-4 lg:px-0">
      <div className="flex items-center gap-2">
        <Skeleton className="h-8 w-8" rounded="lg" />
        <Skeleton className="h-6 w-24" />
      </div>
    </div>

    {/* Price Range Section */}
    <div className="space-y-4 px-4 lg:px-0">
      <div className="flex justify-between items-center">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-5 w-24" rounded="full" />
      </div>
      <div className="py-2">
        <Skeleton className="h-1.5 w-full" rounded="full" />
      </div>
      <div className="flex justify-between">
        <Skeleton className="h-2 w-6" />
        <Skeleton className="h-2 w-6" />
      </div>
    </div>

    {/* Filter Groups */}
    {Array.from({ length: 2 }).map((_, groupIndex) => (
      <div key={groupIndex} className="space-y-3 px-4 lg:px-0">
        <Skeleton className="h-3 w-28 uppercase tracking-widest opacity-60" />
        <div className="space-y-1">
          {Array.from({ length: 5 }).map((_, itemIndex) => (
            <div key={itemIndex} className="flex items-center py-1.5">
              <div className="flex items-center gap-3 flex-1">
                <Skeleton className="h-4 w-4" rounded="md" />
                <div className="flex items-center gap-2">
                  <Skeleton className={`h-3 ${itemIndex % 3 === 0 ? 'w-24' : itemIndex % 3 === 1 ? 'w-32' : 'w-20'}`} />
                  <Skeleton className="h-3 w-6 opacity-40" rounded="sm" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Home Section Skeleton
export const HomeSectionSkeleton: React.FC = () => (
  <div className="py-12 px-4 lg:px-24 space-y-8 max-w-[1440px] mx-auto">
    <div className="flex justify-between items-end">
      <div className="space-y-3">
        <Skeleton className="h-10 w-64 md:w-80" />
        <Skeleton className="h-4 w-48 md:w-64" />
      </div>
      <Skeleton className="h-6 w-24 hidden md:block rounded-full" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
      {Array.from({ length: 4 }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  </div>
);

export default Skeleton;
